import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import jsPDF from 'jspdf';
import { autoTable } from 'jspdf-autotable';

export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

function utf8ToBase64(str: string): string {
  const bytes = new TextEncoder().encode(str);
  const binary = Array.from(bytes, (b) => String.fromCharCode(b)).join('');
  return btoa(binary);
}

async function writeToCacheAndShare(base64Data: string, filename: string): Promise<void> {
  const { uri } = await Filesystem.writeFile({
    path: filename,
    data: base64Data,
    directory: Directory.Cache,
  });
  await Share.share({ title: filename, url: uri, dialogTitle: 'Save or Share' });
}

export async function exportCsvNative(csvString: string, filename: string): Promise<void> {
  await writeToCacheAndShare(utf8ToBase64(csvString), filename);
}

// Chunked so large exports don't blow the argument limit of String.fromCharCode.
function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

// Native spreadsheet export saves a real .xlsx instead of CSV: the Google
// Sheets Android app decodes CSV as Latin-1, so a UTF-8 ₹ shows as "â‚¹" and
// the BOM as a literal "ï»¿". An .xlsx carries its own encoding and renders
// the exact currency symbol in Excel AND Sheets. The builder is dynamically
// imported so fflate stays out of the initial bundle.
export async function exportTableXlsxNative(
  headers: (string | number | null | undefined)[],
  rows: (string | number | null | undefined)[][],
  filename: string,
  title?: string,
  metaRows?: (string | number | null | undefined)[][],
  highlightRows?: Set<number>,
  layout?: import('./xlsxExport').XlsxLayout,
): Promise<void> {
  const { buildXlsx, xlsxFilename } = await import('./xlsxExport');
  await writeToCacheAndShare(bytesToBase64(buildXlsx(headers, rows, title, metaRows, highlightRows, layout)), xlsxFilename(filename));
}

const BRAND_RED: [number, number, number] = [210, 58, 59];
const GREY: [number, number, number] = [71, 85, 105];
const DARK: [number, number, number] = [26, 29, 46];
const HEADER_BG: [number, number, number] = [245, 246, 250];
// Summary-row tints. Subtle on purpose — they group the table, they don't shout.
const SUBTOTAL_BG: [number, number, number] = [246, 247, 251];
const TOTAL_BG: [number, number, number] = [233, 236, 244];

/**
 * Rows to shade so summary lines read differently from data lines.
 * Indices are into the `rows` array passed to {@link exportTablePdfNative}.
 * Supplying this overrides the default "shade the last row" behaviour, which is
 * wrong whenever a table has trailing footnote rows after its total.
 */
export interface PdfRowEmphasis {
  /** Group-subtotal rows — light tint. */
  subtotal?: number[];
  /** Grand-total rows — stronger tint, bold. */
  total?: number[];
  /**
   * Columns right-aligned in every row — header included — so a numeric column
   * reads as one flush-right stack. `jspdf-autotable` left-aligns everything by
   * default, which leaves figure columns ragged.
   */
  alignRight?: number[];
}

// jsPDF's built-in Helvetica only covers Latin-1, so non-Latin-1 currency glyphs
// (notably the rupee sign ₹ / U+20B9) render as garbage. Register a subset Noto
// Sans TTF and use it for the data table so every currency symbol renders. The
// base64 module is dynamically imported so its ~28KB stays out of the initial bundle.
const NOTO_FONT_FILE = 'NotoSans-subset.ttf';
const NOTO_FONT_NAME = 'NotoSans';

// Registers the Unicode font on a fresh jsPDF doc and returns its name, or falls
// back to 'helvetica' (ASCII currencies still render) if the font can't be loaded.
async function registerUnicodeFont(doc: jsPDF): Promise<string> {
  try {
    const { NOTO_SANS_REGULAR_BASE64 } = await import('./notoSansBase64');
    doc.addFileToVFS(NOTO_FONT_FILE, NOTO_SANS_REGULAR_BASE64);
    doc.addFont(NOTO_FONT_FILE, NOTO_FONT_NAME, 'normal');
    return NOTO_FONT_NAME;
  } catch {
    return 'helvetica';
  }
}

export async function exportTablePdfNative(
  title: string,
  subtitle: string,
  headers: string[],
  rows: string[][],
  filename: string,
  totalRow = false,
  highlightRows?: Set<number>,
  emphasis?: PdfRowEmphasis,
): Promise<void> {
  const orientation = headers.length > 5 ? 'landscape' : 'portrait';
  const doc = new jsPDF({ orientation, unit: 'mm', format: 'a4' });

  // Unicode font for the data table so currency glyphs (e.g. ₹) render correctly.
  const tableFont = await registerUnicodeFont(doc);

  // Brand mark
  doc.setFillColor(...BRAND_RED);
  doc.roundedRect(14, 10, 8, 8, 1.5, 1.5, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('M', 18, 15.8, { align: 'center' });

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK);
  doc.text('Maghil', 25, 14.5);
  doc.setFontSize(6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GREY);
  doc.text('ANALYTICS', 25, 18.5);

  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK);
  doc.text(title, 14, 30);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...GREY);
  doc.text(subtitle, 14, 36);

  // Use the embedded Unicode font for the whole table. jsPDF has no synthetic bold
  // for custom fonts, so the header (and the emphasised total row) use the same face
  // (fontStyle 'normal') and stay distinct via their shaded background — this keeps
  // the ₹ glyph intact in the header, which is exactly where the currency symbol
  // appears (e.g. "Amount (₹)").
  const boldFontStyle = tableFont === NOTO_FONT_NAME ? 'normal' : 'bold';
  const lastRowIdx = rows.length - 1;
  // Single-outlet export: totalRow=true highlights only the last body row.
  // Multi-outlet export: highlightRows carries every per-outlet total row's index instead.
  const isHighlighted = (idx: number) => (highlightRows ? highlightRows.has(idx) : totalRow && idx === lastRowIdx);
  // Two-tier emphasis, for tables that have BOTH group subtotals and a grand
  // total. Takes precedence over the single-tier paths above when supplied.
  const subtotalRows = new Set(emphasis?.subtotal ?? []);
  const totalRows = new Set(emphasis?.total ?? []);
  const hasEmphasis = subtotalRows.size > 0 || totalRows.size > 0;
  // Right-aligned columns are handled in the same hook, but apply to the header
  // too — hence the check sits before the body-only guard below.
  const rightCols = new Set(emphasis?.alignRight ?? []);
  const needsCellHook = hasEmphasis || rightCols.size > 0 || totalRow || !!highlightRows;
  autoTable(doc, {
    startY: 42,
    head: [headers],
    body: rows,
    theme: 'grid',
    headStyles: { fillColor: HEADER_BG, textColor: GREY, fontStyle: boldFontStyle, fontSize: 8 },
    bodyStyles: { fontSize: 8.5, textColor: DARK },
    alternateRowStyles: { fillColor: [251, 252, 255] },
    styles: { cellPadding: 3, font: tableFont },
    // Right-align the declared numeric columns, then emphasise summary rows.
    // Callers that know their layout pass explicit indices (subtotals vs grand
    // total); otherwise fall back to highlightRows / last body row.
    didParseCell: needsCellHook
      ? (data) => {
          if (rightCols.has(data.column.index)) data.cell.styles.halign = 'right';
          if (data.section !== 'body') return;
          if (hasEmphasis) {
            if (totalRows.has(data.row.index)) {
              data.cell.styles.fontStyle = boldFontStyle;
              data.cell.styles.fillColor = TOTAL_BG;
            } else if (subtotalRows.has(data.row.index)) {
              data.cell.styles.fontStyle = boldFontStyle;
              data.cell.styles.fillColor = SUBTOTAL_BG;
            }
          } else if (isHighlighted(data.row.index)) {
            data.cell.styles.fontStyle = boldFontStyle;
            data.cell.styles.fillColor = HEADER_BG;
          }
        }
      : undefined,
  });

  const base64 = doc.output('datauristring').split(',')[1];
  await writeToCacheAndShare(base64, filename);
}
